// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ConditionalFormattingAdditionalConditionsPopup.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * @module ConditionalFormattingAdditionalConditionsPopup popup component
 */

import React, { Component } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import { find, getOr } from 'lodash/fp';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import Condition from './Izenda.ConditionalFormattingCondition';
import ConditionType from './Izenda.ConditionalFormattingConditionType';
import ConditionValue from './Izenda.ConditionalFormattingConditionValue';
import InputByDataType from 'share/components/controls/Izenda.InputByDataType';
import Label  from 'share/components/controls/Izenda.Label';
import constants from 'logic/data/Izenda.ConditionalFormattingConstants';

const {CONDITION_TYPE} = constants;

class ConditionalFormattingAdditionalConditionsPopup extends Component {
  additionalConditions = [];

  constructor(props) {
    super(props);

    this.setAdditionalConditions();
  }

  setAdditionalConditions = () => {
    const {props: {additionalFields, additionalConditions }} = this;

    additionalFields.forEach(additionalField => {
      const additionalFieldName = additionalField.name;
      const additionalFieldValue = additionalField.value;
      const conditionField = {
        value: additionalFieldName,
        additionalValue: additionalFieldValue
      };

      const additionalCondition = new Condition();
      additionalCondition.setField(conditionField);

      const existingAdditionalCondition = find(condition => condition.conditionField.name == additionalFieldName, additionalConditions);
      if(existingAdditionalCondition){
        const {type, value} = existingAdditionalCondition;
        additionalCondition.setType(type);
        additionalCondition.setValue(value);
      }

      this.additionalConditions.push(additionalCondition);
    });
  }

  getNotEmptyAdditionalConditions = () => {
    return this.getAdditionalConditionsWithSideTotalFlag().filter(condition => {
      const conditionType = condition.type;
      if(!conditionType)
        return false;

      switch(conditionType){
        case CONDITION_TYPE.ALL_VALUES.value:
        case CONDITION_TYPE.NULL.value:
        case CONDITION_TYPE.BLANK.value:
          return true;
        case CONDITION_TYPE.VALUE.value:
          return !!condition.value;
        case CONDITION_TYPE.RANGE_VALUE.value:
            return !!condition.value.from && !!condition.value.to;
        default:
          return false;
      }
    });
  }

  getAdditionalConditionsWithSideTotalFlag = () => {
    const {props: { isAdditionalConditionsForField, field }, additionalConditions} = this;
    return isAdditionalConditionsForField && field.applyToSideTotal ? additionalConditions.slice(0, -1) : additionalConditions;
  }

  renderAdditionalCondition = (condition, index) => {
    const {props: {localization}} = this;

    const conditionFieldName = getOr('', 'conditionField.name', condition);

    return (
      <GridLayout key={index}>
        <GridLayout.Row>
          <GridLayout.Col>
            <span className="u_textBold">{conditionFieldName}</span>
          </GridLayout.Col>
        </GridLayout.Row>
        <ConditionType condition={condition} localization={localization} isAdditionalCondition={true} />
        <ConditionValue condition={condition} localization={localization} />
        <GridLayout.Row />
        <GridLayout.Row />
      </GridLayout>
    );
  }

  render() {
    const {
      props: { localization, onReject, onAccept, onAdditionalConditionsChange, isAdditionalConditionsForField, field },
      renderAdditionalCondition, getNotEmptyAdditionalConditions,
      getAdditionalConditionsWithSideTotalFlag
    } = this;

    const onClosing = (callback, type) => {
      if(type === 'accept'){
        onAdditionalConditionsChange(getNotEmptyAdditionalConditions());
        onAccept();
      }else{
        onReject();
      }
    };

    const onApplyToSideTotalChange = (value) => {
      field.applyToSideTotal = value.value;
    }    

    return (
      <Popup visible onClosing={onClosing} autoClose={false} type="none" sizeClass="modal-sm">
        <Popup.Header title={localization.getText('CONDITIONAL_FORMATTING_ADDITIONAL_CONDITIONS_POPUP_TITLE')} />
        <Popup.Body>
          {isAdditionalConditionsForField && 
            (
              <div style={{'padding': '0 10px 10px'}}>
                <Label>
                  <span style={{'margin-right': '10px'}}>{localization.getText('CONDITIONAL_FORMATTING_ADDITIONAL_CONDITIONS_APPLY_TO_SIDE_TOTAL')}</span>
                  <InputByDataType value={field.applyToSideTotal} type={'checkbox'} onChange={onApplyToSideTotalChange} />
                </Label>
              </div>
            )
          }
          <div style={{'height': '180px' }}>
            <ScrollBox fullWidth={true}>
              <div style={{'padding': '0 10px'}}>
                {getAdditionalConditionsWithSideTotalFlag().map(renderAdditionalCondition)}
              </div>
            </ScrollBox>
          </div>
        </Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type="accept">{localization.getText('COMMON_LABEL_OK')}</Popup.ConfirmationButton>
          <Popup.ConfirmationButton type="cancel">{localization.getText('COMMON_LABEL_CANCEL')}</Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}

const popupWindow = connect()(ConditionalFormattingAdditionalConditionsPopup);
popupWindow.displayName  = 'ConditionalFormattingAdditionalConditionsPopup';
export default popupWindow;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ConditionalFormatting/Izenda.ConditionalFormattingAdditionalConditionsPopup.jsx